// actions/shiftSwapActions.js

import * as actionTypes from './types';
import fetchData from '../services';

// ============================================================================
// FETCH SWAP ID FROM VISIT
// ============================================================================
export const fetchSwapIdByVisit = (visitId, companyId) => {
    return new Promise((resolve, reject) => {
        const response = fetchData({
            url: `/company/visits/${visitId}/swap-id`,
            method: 'POST',
            data: { companyId }
        });
        response.then(res => {
            if (res.status === 1) {
                resolve(res.data.swapId);
            } else {
                reject(new Error(res.message || 'Swap ID not found'));
            }
        }).catch(error => {
            reject(error);
        });
    });
};

// ============================================================================
// APPROVE SHIFT SWAP (with auto-fetch swapId)
// ============================================================================
export const approveShiftSwap = (swapData) => {
    return async (dispatch) => {
        try {
            // Set loading state
            dispatch({
                type: actionTypes.APPROVE_SWAP_REQUEST,
                payload: { loading: true }
            });

            // If swapId not provided but visitId is, fetch it
            let finalSwapId = swapData.swapId;
            
            if (!finalSwapId && swapData.visitId) {
                console.log('Fetching swapId from visitId:', swapData.visitId);
                
                try {
                    finalSwapId = await fetchSwapIdByVisit(
                        swapData.visitId, 
                        swapData.companyId || sessionStorage.getItem('companyUserId')
                    );
                    console.log('Fetched swapId:', finalSwapId);
                } catch (error) {
                    console.error('Failed to fetch swapId:', error);
                    
                    dispatch({
                        type: actionTypes.APPROVE_SWAP_FAILURE,
                        payload: {
                            success: false,
                            error: error.message || 'No active swap request found for this visit',
                            loading: false
                        }
                    });

                    dispatch({
                        type: actionTypes.BOTTOM_T_W_AL,
                        payload: {
                            show_hide: true,
                            text: `⚠️ ${error.message || 'No active swap request found'}`,
                            alignment: "centre"
                        }
                    });
                    
                    return;
                }
            }

            if (!finalSwapId) {
                throw new Error('Swap ID is required');
            }

            // Additional validation
            if (typeof finalSwapId === 'object') {
                console.warn('⚠️ swapId is an object, extracting swapId property');
                finalSwapId = finalSwapId.swapId || finalSwapId;
            }

            console.log('Approving swap with ID:', finalSwapId);

            // Now make the approve request with swapId
            const response = await fetchData({
                url: '/company/visits/approve-swap',
                method: 'POST',
                data: {
                    swapId: finalSwapId,
                    visitId: swapData.visitId,
                    companyId: swapData.companyId,
                    adminId: swapData.adminId,
                    adminName: swapData.adminName,
                    approvalNotes: swapData.approvalNotes
                }
            });

            console.log('Approve response:', response);

            // ✅ FIX: Check response.status, not just truthy
            if (response.status === 1) {
                // Success
                dispatch({
                    type: actionTypes.APPROVE_SWAP_SUCCESS,
                    payload: {
                        success: true,
                        message: response.message || 'Shift swap approved successfully!',
                        data: response.data,
                        loading: false
                    }
                });

                // Show success toast
                dispatch({
                    type: actionTypes.BOTTOM_T_W_AL,
                    payload: {
                        show_hide: true,
                        text: '✅ ' + (response.message || 'Shift swap approved successfully!'),
                        alignment: "centre"
                    }
                });
            } else {
                // ✅ FIX: This is a failure, not success
                dispatch({
                    type: actionTypes.APPROVE_SWAP_FAILURE,
                    payload: {
                        success: false,
                        error: response.message || 'Failed to approve shift swap',
                        loading: false
                    }
                });

                dispatch({
                    type: actionTypes.BOTTOM_T_W_AL,
                    payload: {
                        show_hide: true,
                        text: `⚠️ ${response.message || 'Failed to approve shift swap'}`,
                        alignment: "centre"
                    }
                });
            }
        } catch (error) {
            console.error('Error in approveShiftSwap:', error);
            
            dispatch({
                type: actionTypes.APPROVE_SWAP_FAILURE,
                payload: {
                    success: false,
                    error: error.message || 'Network error',
                    loading: false
                }
            });

            dispatch({
                type: actionTypes.BOTTOM_T_W_AL,
                payload: {
                    show_hide: true,
                    text: `⚠️ ${error.message || 'Failed to approve shift swap'}`,
                    alignment: "centre"
                }
            });
        }
    };
};

// ============================================================================
// REJECT SHIFT SWAP (with auto-fetch swapId)
// ============================================================================
export const rejectShiftSwap = (swapData) => {
    return async (dispatch) => {
        try {
            // Set loading state
            dispatch({
                type: actionTypes.REJECT_SWAP_REQUEST,
                payload: { loading: true }
            });

            // If swapId not provided but visitId is, fetch it
            let finalSwapId = swapData.swapId;
            
            if (!finalSwapId && swapData.visitId) {
                console.log('Fetching swapId from visitId:', swapData.visitId);
                
                try {
                    finalSwapId = await fetchSwapIdByVisit(
                        swapData.visitId, 
                        swapData.companyId || sessionStorage.getItem('companyUserId')
                    );
                    console.log('Fetched swapId:', finalSwapId);
                } catch (error) {
                    console.error('Failed to fetch swapId:', error);
                    
                    dispatch({
                        type: actionTypes.REJECT_SWAP_FAILURE,
                        payload: {
                            success: false,
                            error: error.message || 'No active swap request found for this visit',
                            loading: false
                        }
                    });

                    dispatch({
                        type: actionTypes.BOTTOM_T_W_AL,
                        payload: {
                            show_hide: true,
                            text: `⚠️ ${error.message || 'No active swap request found'}`,
                            alignment: "centre"
                        }
                    });
                    
                    return;
                }
            }

            if (!finalSwapId) {
                throw new Error('Swap ID is required');
            }

            // Additional validation
            if (typeof finalSwapId === 'object') {
                console.warn('⚠️ swapId is an object, extracting swapId property');
                finalSwapId = finalSwapId.swapId || finalSwapId;
            }

            console.log('Rejecting swap with ID:', finalSwapId);

            // Now make the reject request with swapId
            const response = await fetchData({
                url: '/company/visits/reject-swap',
                method: 'POST',
                data: {
                    swapId: finalSwapId,
                    companyId: swapData.companyId,
                    adminId: swapData.adminId,
                    adminName: swapData.adminName,
                    rejectionReason: swapData.rejectionReason
                }
            });

            console.log('Reject response:', response);

            // ✅ FIX: Check response.status, not just truthy
            if (response.status === 1) {
                // Success
                dispatch({
                    type: actionTypes.REJECT_SWAP_SUCCESS,
                    payload: {
                        success: true,
                        message: response.message || 'Shift swap rejected successfully!',
                        data: response.data,
                        loading: false
                    }
                });

                dispatch({
                    type: actionTypes.BOTTOM_T_W_AL,
                    payload: {
                        show_hide: true,
                        text: '✅ ' + (response.message || 'Shift swap rejected successfully!'),
                        alignment: "centre"
                    }
                });
            } else {
                // ✅ FIX: This is a failure, not success
                dispatch({
                    type: actionTypes.REJECT_SWAP_FAILURE,
                    payload: {
                        success: false,
                        error: response.message || 'Failed to reject shift swap',
                        loading: false
                    }
                });

                dispatch({
                    type: actionTypes.BOTTOM_T_W_AL,
                    payload: {
                        show_hide: true,
                        text: `⚠️ ${response.message || 'Failed to reject shift swap'}`,
                        alignment: "centre"
                    }
                });
            }
        } catch (error) {
            console.error('Error in rejectShiftSwap:', error);
            
            dispatch({
                type: actionTypes.REJECT_SWAP_FAILURE,
                payload: {
                    success: false,
                    error: error.message || 'Network error',
                    loading: false
                }
            });

            dispatch({
                type: actionTypes.BOTTOM_T_W_AL,
                payload: {
                    show_hide: true,
                    text: `⚠️ ${error.message || 'Failed to reject shift swap'}`,
                    alignment: "centre"
                }
            });
        }
    };
};

// ============================================================================
// CLEAR SWAP MESSAGES
// ============================================================================
export const clearSwapMessage = () => {
    return (dispatch) => {
        dispatch({
            type: actionTypes.CLEAR_SWAP_MESSAGE
        });
    };
};